/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import { Delete, GripVertical, Minus, Phone, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MicSelector } from "@/components/ui/mic-selector";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SpeakerSelector } from "@/components/ui/speaker-selector";
import type { Agent } from "@/lib/auth/types";
import { useDialerStore } from "../store";
import { usePlivo } from "../use-plivo";
import { CallControls } from "./call-controls";
import { DialPad } from "./dial-pad";

interface DialerWidgetProps {
	agent: Agent;
}

export function DialerWidget({ agent }: DialerWidgetProps) {
	const { open } = useDialerStore();
	if (!open) return null;
	return <DialerPanel agent={agent} />;
}

function DialerPanel({ agent }: { agent: Agent }) {
	const {
		numberToCall,
		fromNumber,
		countryCode,
		minimized,
		closeDialer,
		setMinimized,
		setNumberToCall,
		setFromNumber,
	} = useDialerStore();

	const [selectedMic, setSelectedMic] = useState("");
	const [isMicMuted, setIsMicMuted] = useState(false);
	const [selectedSpeaker, setSelectedSpeaker] = useState("");
	const constraintsRef = useRef<HTMLDivElement>(null);

	const plivo = usePlivo({
		agent,
		numberToCall,
		countryCode,
		fromNumber,
		playDisclaimer: false,
	});

	useEffect(() => {
		if (selectedSpeaker) plivo.setSpeaker(selectedSpeaker);
	}, [selectedSpeaker]);

	const phoneOptions =
		agent.phones?.map((p) => ({
			value: p.phone,
			label: p.phone_details?.formatted || p.phone,
		})) ?? [];

	const handleDigitPress = (digit: string) => {
		if (plivo.isCalling) {
			plivo.sendDtmf(digit);
		} else {
			setNumberToCall(numberToCall + digit);
		}
	};

	const handleBackspace = () => {
		if (!plivo.isCalling && numberToCall.length > 0) {
			setNumberToCall(numberToCall.slice(0, -1));
		}
	};

	return (
		<div
			ref={constraintsRef}
			className="fixed inset-0 z-50 pointer-events-none"
		>
			<motion.div
				drag
				dragConstraints={constraintsRef}
				dragMomentum={false}
				dragElastic={0.1}
				initial={{ x: 0, y: 0 }}
				style={{ position: "absolute", right: 16, bottom: 16 }}
				className="pointer-events-auto"
			>
				<AnimatePresence mode="wait">
					{minimized ? (
						<motion.div
							key="minimized"
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.8, opacity: 0 }}
							transition={{ type: "spring", stiffness: 400, damping: 25 }}
							onClick={() => setMinimized(false)}
							className={`flex cursor-pointer items-center gap-2.5 rounded-full border px-4 py-2.5 shadow-2xl backdrop-blur-sm select-none ${
								plivo.isCalling
									? "border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/50"
									: "border-border bg-card"
							}`}
						>
							<Phone
								className={`size-4 ${plivo.isCalling ? "text-emerald-600" : "text-muted-foreground"}`}
							/>
							<span className="text-sm font-medium tabular-nums">
								{plivo.isCalling
									? plivo.formatDuration(plivo.callDuration)
									: "Dialer"}
							</span>
						</motion.div>
					) : (
						<motion.div
							key="expanded"
							initial={{ scale: 0.95, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.95, opacity: 0 }}
							transition={{ type: "spring", stiffness: 400, damping: 25 }}
							className="w-80 overflow-hidden rounded-2xl border border-border/50 bg-card shadow-2xl"
						>
							{/* Header */}
							<div className="flex items-center justify-between px-4 py-3">
								<div className="flex items-center gap-1.5">
									<GripVertical className="size-4 cursor-grab text-muted-foreground/40 active:cursor-grabbing" />
									<span className="text-sm font-semibold tracking-tight">
										Dialer
									</span>
									<span
										className={`size-2 rounded-full ${
											plivo.isConnected
												? "bg-emerald-500"
												: "bg-muted-foreground/30"
										}`}
									/>
								</div>
								<div className="flex items-center gap-0.5">
									<Button
										variant="ghost"
										size="icon-xs"
										onClick={() => setMinimized(true)}
										className="text-muted-foreground/60 hover:text-foreground"
									>
										<Minus className="size-3.5" />
									</Button>
									<Button
										variant="ghost"
										size="icon-xs"
										onClick={closeDialer}
										disabled={plivo.isCalling}
										className="text-muted-foreground/60 hover:text-foreground"
									>
										<X className="size-3.5" />
									</Button>
								</div>
							</div>

							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
							>
								{/* Content */}
								<div className="flex flex-col gap-4 px-5 pb-4">
									{phoneOptions.length > 0 && (
										<Select value={fromNumber} onValueChange={setFromNumber}>
											<SelectTrigger size="sm" className="w-full text-xs">
												<SelectValue placeholder="Caller ID" />
											</SelectTrigger>
											<SelectContent>
												{phoneOptions.map((opt) => (
													<SelectItem key={opt.value} value={opt.value}>
														{opt.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}

									{/* Number display */}
									<div className="relative">
										<Input
											value={numberToCall}
											onChange={(e) => setNumberToCall(e.target.value)}
											placeholder="Enter number"
											className="h-12 border-0 bg-transparent pr-8 text-center text-2xl font-semibold tracking-widest shadow-none placeholder:text-muted-foreground/30 placeholder:text-base placeholder:tracking-normal focus-visible:ring-0"
											disabled={plivo.isCalling}
										/>
										{numberToCall && !plivo.isCalling && (
											<Button
												variant="ghost"
												size="icon-xs"
												className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground"
												onClick={handleBackspace}
											>
												<Delete className="size-4" />
											</Button>
										)}
									</div>

									<DialPad
										onDigitPress={handleDigitPress}
										disabled={plivo.isLoading}
									/>

									<CallControls
										isCalling={plivo.isCalling}
										isMuted={plivo.isMuted}
										isNoiseReductionOn={plivo.isNoiseReductionOn}
										callStatus={plivo.callStatus}
										callDuration={plivo.callDuration}
										isLoading={plivo.isLoading || !numberToCall}
										onCall={plivo.makeCall}
										onHangUp={plivo.hangUp}
										onToggleMute={plivo.toggleMute}
										onToggleNoiseReduction={plivo.toggleNoiseReduction}
										formatDuration={plivo.formatDuration}
									/>
								</div>

								{/* Volume indicator */}
								{plivo.isCalling && plivo.callStatus === "Answered" && (
									<div className="flex gap-1 px-5 pb-3">
										<div className="flex flex-1 items-center gap-1.5 text-[10px] text-muted-foreground/60">
											<span>MIC</span>
											<div className="h-1 flex-1 rounded-full bg-muted/50 overflow-hidden">
												<div
													className="h-full rounded-full bg-emerald-500 transition-all duration-75"
													style={{
														width: `${Math.min(plivo.inputVolume * 100, 100)}%`,
													}}
												/>
											</div>
										</div>
									</div>
								)}

								{/* Footer */}
								<div className="flex items-center justify-between border-t border-border/50 px-4 py-2.5">
									<MicSelector
										value={selectedMic}
										onValueChange={setSelectedMic}
										muted={isMicMuted}
										onMutedChange={setIsMicMuted}
										disabled={plivo.isCalling}
										className="w-auto max-w-[45%]"
									/>
									<SpeakerSelector
										value={selectedSpeaker}
										onValueChange={setSelectedSpeaker}
										disabled={plivo.isCalling}
										className="w-auto max-w-[45%]"
									/>
								</div>
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>
		</div>
	);
}
